import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter as Router, Switch, Route, withRouter } from 'react-router-dom';
import ReactGA from 'react-ga';
import { UnAuthedRoute, AuthedRoute } from './route-handlers';
import routePaths from './route-paths';
import TabSwitcher from './components/tab-switcher/tab-switcher';
import NotFoundLayout from './layouts/not-found-layout';
import SignIn from './pages/login/sign-in';
import ForgotPassword from './pages/login/forgot-password';
import Logout from './pages/logout';
import ChangePassword from './pages/login/change-password';
import SetResetPassword from './pages/login/set-reset-password';
import RegisterInvitee from './pages/login/register-invitee';
import EditSite from './pages/supplimentation/edit-site';
import DashboardManager from './pages/holistics-dashboards';
import Sites from './pages/sites';
import { CustomFilters } from './pages/custom-filters';
import UserManagement from './pages/user-management/user-management';
import DataUtilities from './pages/data-utilities';
import { DistributionLists } from './pages/distribution-lists';
import Export from './pages/exports';
import ImportManager from './pages/import-manager';
import ExportManager from './pages/export-manager';
import DataTableManager from './pages/data-table-manager';
import TimebombManager from './pages/timebomb-manager';
import { BuyerPortalAccess } from './pages/import-history';
import MarketplaceManager from './pages/marketplace-manager/marketplace-manager';
import InventoryManager from './pages/inventory-manager/inventory-manager';
import NegotiationManager from './pages/negotiation-manager/negotiation-manager';
import { FeatureFlagManager } from './pages/feature-flag-manager';
import OfferManager from './pages/offer-manager/offer-manager';
import BulkInventoryEditor from './pages/inventory-manager/bulk-inventory-editor';
import { BuyerManager, BuyerManagerDetail } from './pages/buyer-manager';
import ApprovalManager from './pages/data-approval/data-approval';
import CuratedDistributionLists from './pages/curated-distribution-lists/curated-distribution-lists';
import UserSessionManager from './pages/user-session-manager/user-session-manager';
import CategoryManager from './pages/category-manager/category-manager';
import PromptManager from './pages/prompt-manager/prompt-manager';
import SiteHierarchy from './pages/sites/site-hierarchy';
import EditSiteDetails from './pages/sites/edit-site-details';
import { NumocoOrders, NumocoSingleOrder } from './pages/numoco-orders';
import HelloFreshAdminPage from './pages/hello-fresh/hello-fresh-admin-page';
import POManager from './pages/po-manager/po-manager';
import AwaitingPODetail from './pages/po-manager/awaiting-po-detail';
import InProgressDetail from './pages/po-manager/in-progress-detail';
import BillingManagerList from './pages/billing-manager/billing-manager-list';
import BillingManagerEditor from './pages/billing-manager/billing-manager-editor';

@withRouter
class RouteSwitch extends Component {
  static propTypes = {
    location: PropTypes.object.isRequired,
    user: PropTypes.object,
  };

  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      this.onRouteChanged(this.props.location);
    }
  }

  onRouteChanged(location) {
    ReactGA.pageview(`${location.pathname}${location.search}${location.hash}`);
  }

  render() {
    const { user } = this.props;
    return (
      <Switch>
        <UnAuthedRoute exact path={routePaths.signIn} component={SignIn} />
        <UnAuthedRoute exact path={routePaths.forgotPassword} component={ForgotPassword} />
        <UnAuthedRoute exact path={`${routePaths.resetPassword}:token`} component={SetResetPassword} />
        <AuthedRoute exact path={routePaths.home} component={DashboardManager} user={user} />
        <AuthedRoute exact path={routePaths.changePassword} component={ChangePassword} user={user} />
        <UnAuthedRoute exact path={`${routePaths.registerInvitee}:token`} component={RegisterInvitee} />
        <AuthedRoute exact path={routePaths.userManagement} component={UserManagement} user={user} />
        <AuthedRoute exact path={routePaths.editBSR} component={EditSite} user={user} />
        <AuthedRoute exact path={routePaths.data} component={DataUtilities} user={user} />
        <AuthedRoute exact path={routePaths.logout} component={Logout} user={user} />
        <AuthedRoute
          exact
          path={`${routePaths.sites}/:siteId/:locationId`}
          component={EditSiteDetails}
          user={user}
          hideProgressBars={true}
        />
        <AuthedRoute
          exact
          path={`${routePaths.sites}/:siteId`}
          component={SiteHierarchy}
          user={user}
          hideProgressBars={true}
        />
        <AuthedRoute exact path={routePaths.sites} component={Sites} user={user} />
        <AuthedRoute exact path={routePaths.customFilters} component={CustomFilters} user={user} />
        <AuthedRoute exact path={`${routePaths.distributionLists}:siteId`} component={DistributionLists} user={user} />
        <AuthedRoute exact path={routePaths.exports} component={Export} user={user} />
        <AuthedRoute exact path={routePaths.importManager} component={ImportManager} user={user} />
        <AuthedRoute exact path={routePaths.exportManager} component={ExportManager} user={user} />
        <AuthedRoute exact path={routePaths.dataTableManager} component={DataTableManager} user={user} />
        <AuthedRoute exact path={routePaths.timeBombManager} component={TimebombManager} user={user} />
        <AuthedRoute path={routePaths.marketplaceManager} component={MarketplaceManager} user={user} />
        <AuthedRoute exact path={routePaths.buyerPortalAccess} component={BuyerPortalAccess} user={user} />
        <AuthedRoute
          path={routePaths.inventoryManager}
          component={TabSwitcher}
          user={user}
          tabs={[
            { url: '/inventory-editor', title: 'Current cycle inventory', component: InventoryManager },
            {
              url: '/bulk-inventory-editor',
              title: 'Bulk Inventory Editor',
              component: BulkInventoryEditor,
            },
          ]}
        />
        <AuthedRoute path={routePaths.offerManager} component={OfferManager} user={user} />
        <AuthedRoute exact path={routePaths.featureFlagManager} component={FeatureFlagManager} user={user} />
        <AuthedRoute exact path={routePaths.negotiationManager} component={NegotiationManager} user={user} />
        <AuthedRoute exact path={routePaths.buyerManager} component={BuyerManager} user={user} />
        <AuthedRoute exact path={routePaths.curatedNetworkListing} component={CuratedDistributionLists} user={user} />
        <AuthedRoute exact path={routePaths.categoryManager} component={CategoryManager} user={user} />
        <AuthedRoute exact path={routePaths.promptManager} component={PromptManager} user={user} />
        <AuthedRoute
          exact
          path={`${routePaths.buyerManager}/:buyerSiteId`}
          component={BuyerManagerDetail}
          user={user}
        />
        <AuthedRoute exact path={routePaths.numocoOrderManager} component={NumocoOrders} user={user} />
        <AuthedRoute
          exact
          path={`${routePaths.numocoOrderManager}/order/:orderId`}
          component={NumocoSingleOrder}
          user={user}
        />
        <AuthedRoute exact path={routePaths.purchaseOrderManager} component={POManager} user={user} />
        <AuthedRoute
          exact
          path={`${routePaths.purchaseOrderManager}/awaiting-po/:exportRunId`}
          component={AwaitingPODetail}
          user={user}
        />
        <AuthedRoute
          exact
          path={`${routePaths.purchaseOrderManager}/in-progress/:purchaseOrderId`}
          component={InProgressDetail}
          user={user}
        />
        <AuthedRoute exact path={routePaths.approvalManager} component={ApprovalManager} user={user} />
        <AuthedRoute exact path={routePaths.userSessionManager} component={UserSessionManager} user={user} />
        <AuthedRoute exact path={routePaths.billingManager} component={BillingManagerList} user={user} />
        <AuthedRoute
          exact
          path={`${routePaths.billingManager}/org/:orgSiteId`}
          component={BillingManagerEditor}
          user={user}
        />
        <AuthedRoute exact path={routePaths.helloFresh} component={HelloFreshAdminPage} user={user} />
        <Route render={() => <NotFoundLayout />} />
      </Switch>
    );
  }
}

export default class Routes extends Component {
  render() {
    return (
      <Router>
        <RouteSwitch user={this.props.user} />
      </Router>
    );
  }
}

Routes.propTypes = {
  user: PropTypes.object,
};
